import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  Copy,
  Info,
  Loader2,
  RefreshCw,
  Search,
  TriangleAlert,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  FilterBuilder,
  TablePagination,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
  type FilterColumn,
  type FilterColumnType,
  type FilterConjunction,
  type FilterRule,
  type SortAccessors,
} from '@/components/ui/data-table';
import { FixingLoader, FixingOverlay } from '@/components/ui/FixingLoader';
import { SearchTermsWaitState } from '@/components/google-ads/SearchTermsWaitState';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  appliedKey,
  CATEGORY_COPY,
  RecommendationTable,
  SearchTermClusters,
} from '@/components/google-ads/SearchTermClusters';
import { ConvertedBadge } from '@/components/google-ads/ConvertedBadge';
import {
  isConvertingRec,
  recConversions,
  withTermConversions,
} from '@/components/google-ads/convertingSearchTerm';
import { useTenantScopedIdSet } from '@/components/google-ads/useTenantScopedIdSet';
import {
  applySearchTermNegatives,
  isAppServerError,
  isOutcomeUnknown,
  parseApplyInProgress,
  reviewSearchTermsNegatives,
  type ApplySearchTermNegativesResult,
  type BlastRadiusConfirmationDetail,
  type NegativeApplyStatus,
  type SearchTermNegativeRecommendation,
  type SearchTermsReviewResult,
} from '@/services/googleAdsWeeklyService';
import {
  LOST_CONTACT_COPY,
  NEVER_REACHED_COPY,
  RETRY_DROPPED_COPY,
  SUBMITTING_COPY,
  activeApplyCopy,
  appServerErrorCopy,
  checkingCopy,
  notAppliedCopy,
  outcomeCopy,
} from '@/components/google-ads/negativeApplyCopy';
import {
  newIdempotencyKey,
  useNegativeApplyTracker,
} from '@/components/google-ads/useNegativeApplyTracker';
import { operatorQueueCountsQueryKey } from '@/services/googleAdsOperatorQueueService';

type ViewMode = 'grouped' | 'flat';

/**
 * Sort accessors per column key. CPC/CPA are the raw nullable micros fields
 * (exactly what the cells format via formatUsdMicros); nulls sink to the
 * bottom via useTableSort.
 */
const SORT_ACCESSORS: SortAccessors<SearchTermNegativeRecommendation> = {
  negative_keyword: (rec) => rec.negative_keyword,
  category: (rec) => rec.category,
  search_term: (rec) => rec.search_term,
  keyword: (rec) => rec.metrics.triggering_keyword,
  campaign: (rec) => rec.metrics.campaign_name,
  ad_group: (rec) => rec.metrics.ad_group_name,
  spend: (rec) => rec.metrics.cost_micros,
  clicks: (rec) => rec.metrics.clicks,
  impressions: (rec) => rec.metrics.impressions,
  conversions: (rec) => recConversions(rec),
  cpc: (rec) => rec.metrics.cpc_micros,
  cpa: (rec) => rec.metrics.cpa_micros,
};

/** Divide a nullable micros value into dollars for filter comparisons. */
function microsToUsd(value: number | null | undefined): number | null {
  return value == null ? null : value / 1_000_000;
}

type Rec = SearchTermNegativeRecommendation;

const filterCol = (
  key: string,
  label: string,
  type: FilterColumnType,
  accessor: (rec: Rec) => string | number | null | undefined,
  options?: { value: string; label: string }[],
): FilterColumn<Rec> => ({ key, label, type, accessor, options });

/**
 * Condition-filter columns (FilterBuilder). Same accessors as
 * SORT_ACCESSORS, except micros fields are converted to dollars so operators
 * compare against the dollar amounts users type.
 */
const FILTER_COLUMNS: FilterColumn<Rec>[] = [
  filterCol(
    'category',
    'Category',
    'enum',
    (r) => r.category,
    Object.entries(CATEGORY_COPY).map(([value, copy]) => ({
      value,
      label: copy.label,
    })),
  ),
  filterCol('search_term', 'Search term', 'text', (r) => r.search_term),
  filterCol(
    'negative_keyword',
    'Negative keyword',
    'text',
    (r) => r.negative_keyword,
  ),
  filterCol(
    'keyword',
    'Keyword (triggering)',
    'text',
    (r) => r.metrics.triggering_keyword,
  ),
  filterCol('campaign', 'Campaign', 'text', (r) => r.metrics.campaign_name),
  filterCol('ad_group', 'Ad group', 'text', (r) => r.metrics.ad_group_name),
  filterCol('match_type', 'Match type', 'enum', (r) => r.match_type, [
    { value: 'EXACT', label: 'Exact' },
    { value: 'PHRASE', label: 'Phrase' },
    { value: 'BROAD', label: 'Broad' },
  ]),
  filterCol('spend', 'Spend ($)', 'number', (r) =>
    microsToUsd(r.metrics.cost_micros),
  ),
  filterCol('clicks', 'Clicks', 'number', (r) => r.metrics.clicks),
  filterCol('impressions', 'Impressions', 'number', (r) => r.metrics.impressions),
  filterCol('conversions', 'Conversions', 'number', (r) => recConversions(r)),
  filterCol('cpc', 'CPC ($)', 'number', (r) => microsToUsd(r.metrics.cpc_micros)),
  filterCol('cpa', 'CPA ($)', 'number', (r) => microsToUsd(r.metrics.cpa_micros)),
];

/** Default widths (px) for the resizable text columns. */
const RESIZE_DEFAULTS: Record<string, number> = {
  negative_keyword: 180,
  search_term: 220,
  keyword: 170,
  campaign: 170,
  ad_group: 150,
};

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Recognize the frozen 409 contract from the apply route:
 * ``detail = { code: "blast_radius_confirmation_required", assessment, confirmation_phrase }``.
 * Returns null for every other error so normal toast handling applies.
 */
function parseBlastRadiusDetail(
  err: unknown,
): BlastRadiusConfirmationDetail | null {
  const response = (
    err as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail as
    | Partial<BlastRadiusConfirmationDetail>
    | undefined;
  if (!detail || detail.code !== 'blast_radius_confirmation_required') {
    return null;
  }
  const reasons = Array.isArray(detail.assessment?.reasons)
    ? detail.assessment.reasons.filter(
        (reason): reason is string => typeof reason === 'string',
      )
    : [];
  return {
    code: 'blast_radius_confirmation_required',
    assessment: {
      requires_confirmation: Boolean(detail.assessment?.requires_confirmation),
      reasons,
      summary: detail.assessment?.summary ?? {},
    },
    confirmation_phrase:
      typeof detail.confirmation_phrase === 'string'
      && detail.confirmation_phrase.trim()
        ? detail.confirmation_phrase
        : 'APPLY ACCOUNT-WIDE NEGATIVES',
  };
}

type ApplyNoticeTone = 'success' | 'warning' | 'error';

const NOTICE_TONE_CLASSES: Record<ApplyNoticeTone | 'info', string> = {
  success: 'border-border bg-muted text-foreground',
  info: 'border-border bg-warm-bg text-foreground',
  warning: 'border-border bg-warm-bg text-foreground',
  error: 'border-destructive/40 bg-destructive/5 text-destructive',
};

function toneFor(status: NegativeApplyStatus | undefined): ApplyNoticeTone {
  if (status === 'failed') return 'error';
  if (status === 'partial' || status === 'abandoned') return 'warning';
  return 'success';
}

function apiErrorDetail(err: unknown): string {
  const responseDetail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof responseDetail === 'string' && responseDetail.trim()) {
    return responseDetail.trim();
  }
  const detailMessage = (responseDetail as { message?: unknown } | null | undefined)?.message;
  if (typeof detailMessage === 'string' && detailMessage.trim()) {
    return detailMessage.trim();
  }
  if (responseDetail) {
    try {
      return JSON.stringify(responseDetail);
    } catch {
      return String(responseDetail);
    }
  }
  const message = (err as { message?: string })?.message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : 'No error detail was returned by the API.';
}

export function errorGuidance(detail: string): { title: string; note: string } {
  const normalized = detail.toLowerCase();
  if (normalized.includes('not connected for this account')) {
    return {
      title: 'Google Ads is not connected for this account.',
      note: 'Connect Google Ads for this client before pulling search terms — there is nothing to review until that is done.',
    };
  }
  // ⛔ CHECKED BEFORE THE GENERIC SESSION-AUTH CASE BELOW, ON PURPOSE
  // (2026-08-25). Google's own gRPC error text carries strings like
  // "UNAUTHENTICATED" and "authentication_error" as PART OF ITS OWN ERROR
  // TAXONOMY — CUSTOMER_NOT_FOUND is literally reported inside an
  // `authentication_error` field, nothing to do with the operator's Core
  // session. The old broad `includes('authentication')` check ran first and
  // told an operator to "refresh the app, sign in again" for a wrong
  // customer_id — a dead end that fixes nothing. This wrapper prefix is a
  // strong, unambiguous signal that whatever follows is GOOGLE's error, not
  // Core's, so it is checked first and wins.
  if (normalized.includes('search term review failed while reading google ads')) {
    if (
      normalized.includes('customer_not_found')
      || normalized.includes('no customer found')
    ) {
      return {
        title: "Google Ads does not recognize this account's customer ID.",
        note: 'The stored customer ID for this tenant is wrong, a placeholder, or not linked under the agency MCC — check tenant_settings.google_ad_customer_id, not the operator login.',
      };
    }
    return {
      title: 'Google Ads returned an error while reading search terms.',
      note: 'Check the Google Ads connection, customer ID, account permissions, and whether the account has recent search-term rows.',
    };
  }
  if (
    normalized.includes('missing or invalid authorization')
    || normalized.includes('401')
  ) {
    return {
      title: 'The request did not include a valid operator session.',
      note: 'Refresh the app, sign in again, and confirm the selected workspace is the intended tenant.',
    };
  }
  if (normalized.includes('timeout') || normalized.includes('network')) {
    return {
      title: 'The request timed out before the review completed.',
      note: 'Try a shorter date window first, then capture the error if the same tenant keeps timing out.',
    };
  }
  return {
    title: 'The backend returned an error before recommendations loaded.',
    note: 'Capture this error text with the tenant, date window, and whether the account has recent campaign spend.',
  };
}

function reviewStatusCopy(data: SearchTermsReviewResult): {
  title: string;
  note: string;
  className: string;
} {
  if (data.search_terms_reviewed === 0) {
    // ⛔ THREE STATES, NOT ONE GENERIC BLANK. A confirmed-zero account (real
    // impressions checked, there were none) and an unverified blank (the
    // probe itself failed) look identical as "0 reviewed" but mean opposite
    // things to an operator deciding whether to trust this screen — see
    // search_terms_negatives_service.py's zero_reason. AAA Fence read exactly
    // like a broken customer_id would have, until this distinction existed.
    if (data.zero_looks_like_a_bug) {
      return {
        title: 'This looks like a failed read, not a clean account.',
        note: data.zero_reason ?? 'The account had activity in this window but the search-term pull returned nothing.',
        className: 'border-destructive/40 bg-destructive/5 text-destructive',
      };
    }
    if (data.zero_reason) {
      return {
        title: 'Confirmed: no Search/Shopping impressions in this date window.',
        note: data.zero_reason,
        className: 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-300',
      };
    }
    return {
      title: 'No search terms returned for this date window.',
      note: 'Could not verify whether this account is genuinely clean or the read failed — try Last 90 days or another client with live campaigns before treating this as clean.',
      className: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-300',
    };
  }
  if (data.recommendations.length === 0) {
    return {
      title: 'Search terms loaded and no negative recommendations were found.',
      note: 'This is a valid view-only result. Copy the evidence and verify the same date window in Google Ads if this is for smoke evidence.',
      className: 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-300',
    };
  }
  return {
    title: 'Recommendations loaded for operator review.',
    note: 'Review the rows carefully. Applying selected negatives is the only write action on this tab.',
    className: 'border-coral/30 bg-coral-light/40 text-coral-dark',
  };
}

function buildReviewEvidence(
  tenantId: string,
  data: SearchTermsReviewResult,
  selectedCount: number,
): string {
  return [
    'Search Terms/Negatives evidence',
    `Tenant: ${tenantId}`,
    `Customer ID: ${data.customer_id}`,
    `Date window: ${data.start_date} to ${data.end_date}`,
    `Search terms reviewed: ${data.search_terms_reviewed}`,
    `Recommended negatives: ${data.recommendations.length}`,
    `Selected to apply: ${selectedCount}`,
    `Summary: ${data.client_safe_summary}`,
  ].join('\n');
}

function buildErrorEvidence(
  tenantId: string,
  dateWindow: { startDate: string; endDate: string },
  detail: string,
): string {
  return [
    'Search Terms/Negatives error evidence',
    `Tenant: ${tenantId}`,
    `Date window: ${dateWindow.startDate} to ${dateWindow.endDate}`,
    `Error: ${detail}`,
  ].join('\n');
}

function metricCard(label: string, value: string, icon?: JSX.Element) {
  return (
    <div className="rounded border border-border bg-warm-bg p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export function SearchTermsNegativesTab({ tenantId }: { tenantId: string }) {
  const [windowDays, setWindowDays] = useState('30');
  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
  const [filterConjunction, setFilterConjunction] =
    useState<FilterConjunction>('and');
  // Both sets belong to the client they were ticked for
  // (useTenantScopedIdSet): recommendation ids name no client, so a plain
  // set let a row ticked for one client be applied to the next one.
  const [selectedIds, setSelectedIds] = useTenantScopedIdSet(tenantId);
  // Converting rows the operator ticked by hand, one by one, on the data now
  // on screen (review of #3045, finding C). A converting row is applied only
  // when it is in here: no bulk action and no carried-over selection can put
  // one in the apply. Emptied whenever the data reloads.
  const [convertingTicks, setConvertingTicks] = useTenantScopedIdSet(tenantId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(true);
  // Grouped is the default — Art's spoon-fed cluster view (client-UI item 5).
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  // Non-null while the 409 blast-radius breaker is demanding typed
  // confirmation; the confirm dialog switches into confirmation mode.
  const [blastRadius, setBlastRadius] =
    useState<BlastRadiusConfirmationDetail | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  // Session-persistent applied state (survives the stateless review re-pull).
  // Key = `${negative_keyword.toLowerCase()}|${match_type}` (durable applied
  // unit; recommendation ids can churn across refetches). Reset when the
  // tenant or date window changes (below).
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(new Set());
  // Kitchen-rail default: applied rows come off the queue. Toggled by the
  // "N applied — show" chip so the confirmation view stays one click away.
  const [showApplied, setShowApplied] = useState(false);
  // Same pattern for rejected ("No") rows — the backend already excludes
  // rejected recs from the sidebar badge count (search_terms_easy), so hiding
  // them here by default keeps the badge and the page in sync. Toggled by the
  // "N rejected — show" chip so the operator can reverse a No if needed.
  const [showRejected, setShowRejected] = useState(false);
  // The outcome of the last apply, in plain language, until dismissed.
  const [applyNotice, setApplyNotice] = useState<{
    tone: ApplyNoticeTone;
    text: string;
  } | null>(null);
  // The key for the open confirm dialog; reused for its blast-radius resend
  // and every retry, so one decision can never start two applies.
  const [applyKey, setApplyKey] = useState<string | null>(null);
  // What was last sent, so "Retry" resends exactly that under the same key.
  // Dropped on every reload (dropRetry) and on a client switch.
  const lastSubmission = useRef<{
    recs: SearchTermNegativeRecommendation[];
    key: string;
    confirmationText?: string;
    tenantId: string;
  } | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setAppliedKeys(new Set());
    setApplyNotice(null);
    setShowApplied(false);
    setShowRejected(false);
  }, [tenantId, windowDays]);

  // Most expensive terms first — most useful default for review.
  const { sortKey, sortDir, toggleSort, sortRows } =
    useTableSort<SearchTermNegativeRecommendation>('spend', 'desc');
  const { widths, startResize } = useColumnResize(RESIZE_DEFAULTS);

  const dateWindow = useMemo(() => {
    const days = Number.parseInt(windowDays, 10) || 30;
    return { startDate: isoDaysAgo(days), endDate: todayIso() };
  }, [windowDays]);

  const review = useQuery({
    queryKey: [
      'google-ads-search-terms-negatives',
      tenantId,
      dateWindow.startDate,
      dateWindow.endDate,
    ],
    queryFn: () =>
      reviewSearchTermsNegatives({
        tenantId,
        startDate: dateWindow.startDate,
        endDate: dateWindow.endDate,
        // No limit: Art's PULL step reviews ALL search terms in the window.
        // The history here is a whack-a-mole of caps (500 → 1,000 → 5,000),
        // each one silently dropping the low-cost tail — which is exactly
        // where the junk worth excluding lives. Omitting the param asks the
        // backend for everything.
      }),
    enabled: Boolean(tenantId),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!review.isSuccess) return;
    queryClient.invalidateQueries({
      queryKey: operatorQueueCountsQueryKey(tenantId),
    });
  }, [queryClient, review.dataUpdatedAt, review.isSuccess, tenantId]);

  // Every recommendation of one search term shares its conversions, so a
  // term that converted in another campaign is converting on every row
  // (finding B). Memoised: the same data gives the same array.
  const recommendations = useMemo(
    () => withTermConversions(review.data?.recommendations ?? []),
    [review.data],
  );
  // Seed durable applied-state from the backend (audit-log backed
  // already_applied) so badges + hiding survive hard reloads. Union into the
  // session set — never clobber keys applied moments ago in this session.
  useEffect(() => {
    const durable = (review.data?.recommendations ?? []).filter(
      (rec) => rec.already_applied,
    );
    if (!durable.length) return;
    setAppliedKeys((prev) => {
      const next = new Set(prev);
      durable.forEach((rec) => next.add(appliedKey(rec)));
      return next.size === prev.size ? prev : next;
    });
  }, [review.data]);
  // Recommendation ids whose durable key is already applied — keeps applied
  // rows out of every selection path AND (by default) out of the queue.
  const appliedIdSet = useMemo(
    () =>
      new Set(
        recommendations
          .filter((rec) => appliedKeys.has(appliedKey(rec)))
          .map((rec) => rec.id),
      ),
    [recommendations, appliedKeys],
  );
  // Rejected recs (previously clicked "No") come from the same live query
  // Google returns; the backend stamps `rejected=true` on them from the
  // stored snapshot's rejected_ids. Count them for the toggle so the chip
  // can say "N rejected — show".
  const rejectedIdSet = useMemo(
    () =>
      new Set(
        recommendations.filter((rec) => rec.rejected).map((rec) => rec.id),
      ),
    [recommendations],
  );
  // Converting search terms (shown only as out-of-area since #3048, flagged
  // "Converted N times"): never picked by any Select all control.
  const convertingIdSet = useMemo(
    () =>
      new Set(
        recommendations
          .filter((rec) => isConvertingRec(rec))
          .map((rec) => rec.id),
      ),
    [recommendations],
  );
  // Finding C: a selection belongs to the data it was made on. Whenever the
  // review reloads (Refresh, a new date window, an apply's refetch), keep
  // only rows that are still there, still open and not converting, and drop
  // every hand tick of a converting row. A recommendation id does not change
  // when its term starts converting, so without this a row ticked at 0
  // conversions stayed ticked, and was applied, once it converted.
  useEffect(() => {
    const live = new Map(recommendations.map((rec) => [rec.id, rec]));
    setSelectedIds((prev) => {
      const next = new Set(
        [...prev].filter((id) => {
          const rec = live.get(id);
          return (
            rec !== undefined
            && !appliedKeys.has(appliedKey(rec))
            && !isConvertingRec(rec)
          );
        }),
      );
      return next.size === prev.size ? prev : next;
    });
    setConvertingTicks((prev) => (prev.size ? new Set() : prev));
    // Only a reload prunes; appliedKeys changing on its own must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review.dataUpdatedAt, tenantId, windowDays]);
  // Applied rows leave the queue unless the operator asks for them. Google's
  // search-term report is historical, so applied terms never drop out of the
  // source data on refresh — this filter is what shrinks the queue as work
  // gets done (Art's review feedback, 2026-07-13).
  //
  // Rejected rows follow the same pattern: the operator already said "No"
  // (persisted to the snapshot's rejected_ids), so they leave the queue by
  // default. Hiding them here matches the sidebar badge count exactly — that
  // count also excludes rejected on the backend.
  const workingSet = recommendations.filter((rec) => {
    if (!showApplied && appliedIdSet.has(rec.id)) return false;
    if (!showRejected && rejectedIdSet.has(rec.id)) return false;
    return true;
  });
  // Pipeline: hide-applied → condition rules → sort → paginate. (Category
  // filtering lives in the FilterBuilder — Category is one of its enum columns.)
  const filteredRecommendations = applyFilterRules(
    workingSet,
    filterRules,
    FILTER_COLUMNS,
    filterConjunction,
  );
  const sortedRecommendations = sortRows(
    filteredRecommendations,
    SORT_ACCESSORS,
  );
  const pagination = usePagination(sortedRecommendations.length, {
    defaultPageSize: 20,
  });
  const pageRows = pagination.slice(sortedRecommendations);
  // What an apply sends: ticked rows, and a converting row only when it was
  // ticked by hand on this data (finding C).
  const selectedRecommendations = recommendations.filter(
    (rec) =>
      selectedIds.has(rec.id)
      && (!convertingIdSet.has(rec.id) || convertingTicks.has(rec.id)),
  );
  const selectedIdSet = new Set(selectedRecommendations.map((rec) => rec.id));
  const reviewStatus = review.data ? reviewStatusCopy(review.data) : null;
  const emptyMessage = review.data
    ? recommendations.length === 0
      ? review.data.search_terms_reviewed === 0
        ? (review.data.zero_reason ?? 'No search terms returned for this date window.')
        : 'Search terms loaded; no negative keyword recommendations were found.'
      : workingSet.length === 0
        ? `All ${recommendations.length} recommendations are applied. Use "show" next to the filters to review them.`
        : 'No negative keyword recommendations for this filter.'
    : '';
  const errorDetail = review.isError ? apiErrorDetail(review.error) : '';
  const errorCopy = errorDetail ? errorGuidance(errorDetail) : null;
  const evidenceText = review.data
    ? buildReviewEvidence(tenantId, review.data, selectedRecommendations.length)
    : errorDetail
      ? buildErrorEvidence(tenantId, dateWindow, errorDetail)
      : '';

  // Everything a finished apply can change. Weekly runs are not among them:
  // applies no longer create weekly run rows.
  const refreshAfterApply = () => {
    queryClient.invalidateQueries({
      queryKey: operatorQueueCountsQueryKey(tenantId),
    });
    queryClient.invalidateQueries({
      queryKey: ['google-ads-search-terms-negatives', tenantId],
    });
  };

  const closeAndClearSelection = () => {
    setConfirmOpen(false);
    setBlastRadius(null);
    setConfirmationText('');
    setSelectedIds(new Set());
    setConvertingTicks(new Set());
  };

  /** The one place an apply's final result lands, from the POST or a poll. */
  const applyFinished = (
    status: NegativeApplyStatus | undefined,
    result: ApplySearchTermNegativesResult | null | undefined,
  ) => {
    // Mark exactly what the server says is in Google Ads, never the whole
    // submission: a keyword Google refused must stay in the queue.
    const keys = result?.applied_keys ?? [];
    if (keys.length) {
      setAppliedKeys((prev) => {
        const next = new Set(prev);
        keys.forEach((key) => next.add(key));
        return next;
      });
    }
    const tone = toneFor(status);
    const text = outcomeCopy(status, result);
    toast[tone](text);
    setApplyNotice({ tone, text });
    closeAndClearSelection();
    refreshAfterApply();
  };

  const tracker = useNegativeApplyTracker(tenantId, (record) =>
    applyFinished(record.status, record.result),
  );

  const applyMutation = useMutation({
    // The exact rows and key sent are the mutate() variables, so a retry
    // resends precisely those.
    mutationFn: (vars: {
      recs: SearchTermNegativeRecommendation[];
      key: string;
      confirmationText?: string;
      tenantId: string;
    }) =>
      applySearchTermNegatives({
        // The client the rows were picked for, never whichever is open now.
        tenantId: vars.tenantId,
        selected: vars.recs.map((rec) => ({
          recommendation_id: rec.id,
          negative_keyword: rec.negative_keyword,
          match_type: rec.match_type,
          category: rec.category,
        })),
        rejectedRecommendationIds: recommendations
          .filter((rec) => !vars.recs.some((sent) => sent.id === rec.id))
          .map((rec) => rec.id),
        confirmed: true,
        // Only sent while the dialog is in blast-radius confirmation mode.
        confirmationText: vars.confirmationText,
        idempotencyKey: vars.key,
      }),
    onMutate: (vars) => {
      lastSubmission.current = vars;
      setApplyNotice(null);
    },
    onSuccess: (result) => {
      applyFinished(result.status ?? 'success', result);
      if (result.blast_radius_required) {
        toast.info('Blast-radius safety gate confirmed for this apply.');
      }
    },
    onError: (err: unknown, vars) => {
      const blast = parseBlastRadiusDetail(err);
      if (blast) {
        // Keep the dialog open and switch it into typed-confirmation mode.
        setBlastRadius(blast);
        setConfirmationText('');
        return;
      }
      // Every error clears the typed phrase: it must be retyped on purpose.
      setConfirmationText('');
      const running = parseApplyInProgress(err);
      if (running) {
        // This key (or another operator's apply) is already running: follow
        // it instead of failing, and leave nothing one click away.
        closeAndClearSelection();
        tracker.check(running.idempotency_key, {
          startedAt: running.started_at,
          stage: running.progress?.stage,
        });
        return;
      }
      if (isOutcomeUnknown(err)) {
        // It may still be writing: follow the key, never say "Apply failed".
        closeAndClearSelection();
        tracker.check(vars.key);
        return;
      }
      const detail = apiErrorDetail(err);
      const code = (
        err as { response?: { data?: { detail?: { code?: unknown } } } }
      )?.response?.data?.detail?.code;
      if (isAppServerError(err) && code !== 'apply_ledger_unavailable') {
        // The request is over, but writes may have landed before the error.
        const text = appServerErrorCopy(detail);
        toast.error(text);
        setApplyNotice({ tone: 'error', text });
        refreshAfterApply();
        return;
      }
      const text = notAppliedCopy(detail);
      toast.error(text);
      setApplyNotice({ tone: 'error', text });
    },
  });

  const openConfirm = () => {
    setApplyKey(newIdempotencyKey());
    setConfirmOpen(true);
  };

  const submitApply = () => {
    applyMutation.mutate({
      recs: selectedRecommendations,
      key: applyKey ?? newIdempotencyKey(),
      confirmationText: blastRadius ? confirmationText.trim() : undefined,
      tenantId,
    });
  };

  const retryApply = () => {
    const last = lastSubmission.current;
    tracker.reset();
    // A retry resends exactly what was sent, only for that client, and only
    // while the list it was ticked on is still the one on screen
    // (dropRetry). Otherwise there is nothing to resend: say so, rather
    // than leave a Retry that does nothing.
    if (!last || last.tenantId !== tenantId) {
      lastSubmission.current = null;
      setApplyNotice({ tone: 'warning', text: RETRY_DROPPED_COPY });
      return;
    }
    applyMutation.mutate(last);
  };

  /**
   * A Retry is a selection too (finding C). The apply it resends never
   * reached the server, so a Retry after a reload would be the first real
   * write, made from rows ticked on data that is no longer on screen: a
   * term ticked at 0 conversions that has converted since (and now shows
   * unticked) would go out as a live negative. So a reload drops it, like
   * every tick. A Retry on screen goes away. An apply still being checked
   * keeps its tracker; if it turns out never to have arrived, its Retry
   * finds nothing to resend.
   */
  const dropRetry = () => {
    lastSubmission.current = null;
    if (tracker.tracking.kind === 'never_reached') tracker.reset();
  };
  useEffect(() => {
    dropRetry();
    // A reload only: new review data, or a new date window (at once, before
    // its data lands). tracker is a new object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review.dataUpdatedAt, windowDays]);

  // Switching client starts from nothing: no ticked row, no open confirm
  // dialog and no Retry of the previous client's apply can reach this one.
  // (The selection sets are also scoped to their client, so even the render
  // before this runs shows nothing ticked.)
  const shownTenant = useRef(tenantId);
  useEffect(() => {
    if (shownTenant.current === tenantId) return;
    shownTenant.current = tenantId;
    setSelectedIds(new Set());
    setConvertingTicks(new Set());
    setConfirmOpen(false);
    setBlastRadius(null);
    setConfirmationText('');
    setApplyKey(null);
    lastSubmission.current = null;
    tracker.reset();
    // Only a client switch resets; tracker.reset is a new function each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const trackingCopy = (() => {
    if (tracker.tracking.kind === 'never_reached') return NEVER_REACHED_COPY;
    if (tracker.tracking.kind === 'checking') {
      if (tracker.lostContact) return LOST_CONTACT_COPY;
      const started = tracker.startedAt
        ? new Date(tracker.startedAt).getTime()
        : tracker.since ?? Date.now();
      return checkingCopy(Date.now() - started, tracker.stage);
    }
    if (tracker.activeApply) {
      return activeApplyCopy(tracker.activeApply.started_at, tracker.activeApply.actor);
    }
    return null;
  })();

  const toggleRecommendation = (id: string) => {
    // Applied rows are read-only markers — never selectable.
    if (appliedIdSet.has(id)) return;
    const selecting = !selectedIdSet.has(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selecting) next.add(id);
      else next.delete(id);
      return next;
    });
    // The one way a converting row gets into an apply: its own checkbox.
    if (convertingIdSet.has(id)) {
      setConvertingTicks((prev) => {
        const next = new Set(prev);
        if (selecting) next.add(id);
        else next.delete(id);
        return next;
      });
    }
  };

  /**
   * Bulk toggle shared by the flat header checkbox (all filtered rows), the
   * grouped select-all, and each cluster checkbox — all mutate the same
   * selectedIds set, so the selected-count card and apply payload are
   * identical in both views.
   */
  const toggleRows = (ids: string[], select: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids
        .filter((id) => !appliedIdSet.has(id))
        // A bulk select never picks a converting term (ticked one by one).
        // Clearing still clears it.
        .filter((id) => !select || !convertingIdSet.has(id))
        .forEach((id) => {
          if (select) next.add(id);
          else next.delete(id);
        });
      return next;
    });
    if (!select) {
      setConvertingTicks((prev) => {
        if (!ids.some((id) => prev.has(id))) return prev;
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  /** Closing the confirm dialog always resets blast-radius confirmation. */
  const handleConfirmOpenChange = (open: boolean) => {
    // Esc / backdrop click must not dismiss the dialog mid-write — the apply
    // keeps running either way, so closing here only loses the confirmation
    // surface without stopping anything (Viktor, PR #2034/#2035 follow-up).
    if (applyMutation.isPending) return;
    if (open) {
      openConfirm();
      return;
    }
    setConfirmOpen(open);
    if (!open) {
      setBlastRadius(null);
      setConfirmationText('');
    }
  };

  const confirmationMatches =
    blastRadius !== null
    && confirmationText.trim() === blastRadius.confirmation_phrase;

  const handleFilterRulesChange = (rules: FilterRule[]) => {
    setFilterRules(rules);
    pagination.setPage(1);
  };

  const handleFilterConjunctionChange = (conjunction: FilterConjunction) => {
    setFilterConjunction(conjunction);
    pagination.setPage(1);
  };

  const handleRefresh = () => {
    pagination.setPage(1);
    // Nothing ticked survives a reload (finding C): the rows are re-read.
    // The Retry goes now too, not when the new rows land, so it cannot be
    // clicked while they are being read.
    setSelectedIds(new Set());
    setConvertingTicks(new Set());
    dropRetry();
    review.refetch();
  };

  const copyEvidence = async () => {
    if (!evidenceText) return;
    try {
      await navigator.clipboard.writeText(evidenceText);
      toast.success('Copied Search Terms evidence');
    } catch {
      toast.error('Could not copy evidence from this browser');
    }
  };

  return (
    <div className="relative space-y-4">
      <FixingOverlay
        show={applyMutation.isPending}
        label="Applying negative keywords…"
      />
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Pull recent Google Ads search terms for operator review.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={windowDays} onValueChange={setWindowDays}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={review.isFetching}
              >
                {review.isFetching ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {review.isLoading ? (
            <SearchTermsWaitState windowDays={windowDays} />
          ) : review.isError ? (
            <div className="rounded border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-medium">Search term review failed.</div>
                  {errorCopy && (
                    <div className="mt-2 rounded border border-destructive/20 bg-background/70 p-3">
                      <div className="font-medium">{errorCopy.title}</div>
                      <div className="mt-1 text-xs">{errorCopy.note}</div>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyEvidence}
                  className="border-destructive/30 bg-background text-destructive hover:bg-destructive/10"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy evidence
                </Button>
              </div>
              <div className="mt-1 [overflow-wrap:anywhere]">
                {errorDetail}
              </div>
            </div>
          ) : review.data ? (
            <>
              <Collapsible open={summaryOpen} onOpenChange={setSummaryOpen}>
                <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                      summaryOpen ? '' : '-rotate-90'
                    }`}
                  />
                  Review summary
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    {metricCard(
                      'Search terms reviewed',
                      String(review.data.search_terms_reviewed),
                      <Search className="h-3 w-3" />,
                    )}
                    {metricCard(
                      'Recommended negatives',
                      String(recommendations.length),
                      <TriangleAlert className="h-3 w-3" />,
                    )}
                    {metricCard(
                      'Selected to apply',
                      String(selectedRecommendations.length),
                      <CheckCircle2 className="h-3 w-3" />,
                    )}
                    {metricCard(
                      'Applied',
                      String(appliedKeys.size),
                      <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />,
                    )}
                    {metricCard(
                      'Date window',
                      `${review.data.start_date} to ${review.data.end_date}`,
                    )}
                  </div>

                  <div className="rounded border border-border bg-warm-bg p-3 text-sm">
                    {review.data.client_safe_summary}
                  </div>

                  {reviewStatus && (
                    <div
                      className={`flex flex-wrap items-start justify-between gap-3 rounded border p-3 text-sm ${reviewStatus.className}`}
                    >
                      <div className="flex min-w-0 gap-2">
                        <Info className="mt-0.5 h-4 w-4 flex-none" />
                        <div>
                          <div className="font-medium">{reviewStatus.title}</div>
                          <div className="mt-1 text-xs">{reviewStatus.note}</div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={copyEvidence}
                        className="bg-background/80"
                      >
                        <Copy className="mr-2 h-4 w-4" />
                        Copy evidence
                      </Button>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>

              {trackingCopy && (
                <div
                  role="status"
                  className={`flex flex-wrap items-center justify-between gap-3 rounded border p-3 text-sm ${NOTICE_TONE_CLASSES.info}`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {tracker.tracking.kind === 'checking' && <FixingLoader iconOnly />}
                    <span>{trackingCopy}</span>
                  </div>
                  {tracker.tracking.kind === 'never_reached' && (
                    <Button type="button" size="sm" variant="outline" onClick={retryApply}>
                      Retry
                    </Button>
                  )}
                </div>
              )}

              {applyNotice && !trackingCopy && (
                <div
                  role="status"
                  className={`flex flex-wrap items-start justify-between gap-3 rounded border p-3 text-sm ${NOTICE_TONE_CLASSES[applyNotice.tone]}`}
                >
                  <div className="flex min-w-0 gap-2">
                    {applyNotice.tone === 'error'
                      ? <TriangleAlert className="mt-0.5 h-4 w-4 flex-none" />
                      : <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-coral" />}
                    <span>{applyNotice.text}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Dismiss apply result"
                    onClick={() => setApplyNotice(null)}
                    className="h-7 w-7 flex-none"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Tabs
                    value={viewMode}
                    onValueChange={(value) => setViewMode(value as ViewMode)}
                  >
                    <TabsList className="h-9">
                      <TabsTrigger value="grouped" className="text-xs">
                        Grouped
                      </TabsTrigger>
                      <TabsTrigger value="flat" className="text-xs">
                        Flat
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <FilterBuilder
                    columns={FILTER_COLUMNS}
                    rules={filterRules}
                    conjunction={filterConjunction}
                    onRulesChange={handleFilterRulesChange}
                    onConjunctionChange={handleFilterConjunctionChange}
                  />
                  {appliedIdSet.size > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowApplied((prev) => !prev)}
                      className="h-9 gap-1 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-xs text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100"
                    >
                      <Check className="h-3 w-3" />
                      {appliedIdSet.size} applied — {showApplied ? 'hide' : 'show'}
                    </Button>
                  )}
                  {rejectedIdSet.size > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRejected((prev) => !prev)}
                      className="h-9 gap-1 border-muted-foreground/30 bg-muted/40 text-xs text-muted-foreground hover:bg-muted"
                      title="Rows you (or a teammate) previously said No on. The sidebar badge already excludes these — this toggle lets you reverse a No."
                    >
                      <X className="h-3 w-3" />
                      {rejectedIdSet.size} rejected — {showRejected ? 'hide' : 'show'}
                    </Button>
                  )}
                </div>
                <Button
                  onClick={openConfirm}
                  disabled={
                    !selectedRecommendations.length
                    || applyMutation.isPending
                    || tracker.busy
                  }
                >
                  Apply selected negatives
                </Button>
              </div>

              {viewMode === 'grouped' ? (
                <SearchTermClusters
                  rows={sortedRecommendations}
                  selectedIds={selectedIdSet}
                  appliedKeys={appliedKeys}
                  onToggleRow={toggleRecommendation}
                  onToggleRows={toggleRows}
                  sort={{ sortKey, sortDir, onSort: toggleSort }}
                  widths={widths}
                  onResizeStart={startResize}
                  emptyMessage={emptyMessage}
                />
              ) : (
                <>
                  <div className="rounded border border-border">
                    <RecommendationTable
                      rows={pageRows}
                      selectionRows={filteredRecommendations}
                      selectedIds={selectedIdSet}
                      appliedKeys={appliedKeys}
                      onToggleRow={toggleRecommendation}
                      onToggleRows={toggleRows}
                      sort={{ sortKey, sortDir, onSort: toggleSort }}
                      widths={widths}
                      onResizeStart={startResize}
                      containerClassName="max-h-[60vh]"
                      emptyMessage={emptyMessage}
                    />
                  </div>

                  {sortedRecommendations.length > 0 && (
                    <TablePagination
                      page={pagination.page}
                      pageCount={pagination.pageCount}
                      pageSize={pagination.pageSize}
                      totalRows={sortedRecommendations.length}
                      onPageChange={pagination.setPage}
                      onPageSizeChange={pagination.setPageSize}
                    />
                  )}
                </>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={handleConfirmOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply selected negatives?</DialogTitle>
            <DialogDescription>
              This adds {selectedRecommendations.length} selected exclusion
              {selectedRecommendations.length === 1 ? '' : 's'} to the
              account-wide shared negative list and attaches the list to active
              campaigns as needed. This is a Google Ads write unless the backend
              deploy is running in dry-run mode.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-56 overflow-auto rounded border border-border bg-warm-bg p-3 text-sm">
            {selectedRecommendations.map((rec) => (
              <div key={rec.id} className="flex justify-between gap-3 py-1">
                <span className="flex flex-wrap items-center gap-2">
                  {rec.negative_keyword}
                  <ConvertedBadge rec={rec} />
                </span>
                <span className="text-xs text-muted-foreground">
                  {rec.match_type}
                </span>
              </div>
            ))}
          </div>
          {applyMutation.isPending && (
            <div className="flex items-center gap-2 rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
              <FixingLoader iconOnly />
              {SUBMITTING_COPY}
            </div>
          )}
          {blastRadius && (
            <div className="space-y-3 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-300">
              <div className="flex items-center gap-2 font-medium">
                <TriangleAlert className="h-4 w-4 flex-none" />
                Additional confirmation required (blast-radius safety gate)
              </div>
              {blastRadius.assessment.reasons.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-xs">
                  {blastRadius.assessment.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
              <div className="space-y-1">
                <Input
                  value={confirmationText}
                  onChange={(event) => setConfirmationText(event.target.value)}
                  placeholder={blastRadius.confirmation_phrase}
                  className="bg-card"
                  autoFocus
                />
                <div className="text-xs text-amber-900 dark:text-amber-300/80">
                  Type{' '}
                  <code className="rounded bg-card px-1 py-0.5 text-[11px]">
                    {blastRadius.confirmation_phrase}
                  </code>{' '}
                  exactly, then apply again.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleConfirmOpenChange(false)}
              disabled={applyMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={submitApply}
              disabled={
                applyMutation.isPending
                || (blastRadius !== null && !confirmationMatches)
              }
            >
              {applyMutation.isPending && <FixingLoader iconOnly />}
              {applyMutation.isPending ? 'Applying…' : 'Confirm and apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
